import { getToken } from '@coverright/shared/keycloak';
import {
  StreamCallbacks,
  WsAuthMessage,
  WsChatRequest,
  WsStreamEvent,
} from './chat-ai-types';
import { environment } from '../../../../environments/environment';

// ─── Types ────────────────────────────────────────────────────────────────────

type SessionState =
  | 'connecting'      // WebSocket not yet open
  | 'authenticating'  // open, auth message sent, waiting for auth_ok
  | 'ready'           // auth_ok received, can send questions
  | 'busy'            // question sent, waiting for done
  | 'reconnecting'    // connection lost, waiting to retry
  | 'closed';         // permanently closed by caller

interface PendingQuestion {
  request: WsChatRequest;
  callbacks: StreamCallbacks;
  /** True once at least one token was received — used to decide whether to retry */
  tokensReceived: boolean;
}

export interface ChatSession {
  /**
   * Send a question and stream the response via callbacks.
   * If a response is already in progress, the question is queued and sent
   * automatically once the current `done` event arrives.
   * Returns a cancel function — aborts the pending/active question.
   */
  ask(request: WsChatRequest, callbacks: StreamCallbacks): () => void;

  /** Permanently close the WebSocket. No further questions can be sent. */
  close(): void;
}

// ─── createChatSession ────────────────────────────────────────────────────────

const MAX_RECONNECT_RETRIES = 3;
const RECONNECT_DELAY_MS = 1_000;

/**
 * Create a persistent WebSocket session.
 *
 * Protocol:
 *   connect → send {type:"auth", token} → await auth_ok
 *   → send {question, bid_id?} → await done
 *   → repeat from "send question" for subsequent asks
 *
 * Auto-reconnects (with re-auth) on:
 *   • unexpected close / socket error
 *   • error { code: "auth_failed" }  (refreshes JWT first)
 *   • error { code: "timeout" }
 *
 * Does NOT reconnect on:
 *   • error { code: "validation_error" }
 *   • deliberate close()
 */
export function createChatSession(): ChatSession {
  let ws: WebSocket | null = null;
  let state: SessionState = 'connecting';
  let reconnectAttempts = 0;

  // Getter defeats TS control-flow narrowing after assignments:
  // after `state = 'connecting'`, TS narrows state to that literal and flags
  // `state === 'closed'` as always-false. The function call breaks the narrowing.
  const getState = (): SessionState => state;

  // Queue of at most one pending question (server enforces one-at-a-time)
  let pending: PendingQuestion | null = null;

  // ── Connect ────────────────────────────────────────────────────────────────

  async function connect(): Promise<void> {
    if (getState() === 'closed') return;

    state = 'connecting';

    const token = await getToken();
    if (!token) {
      failPending('auth_failed', 'No auth token available');
      return;
    }

    if (getState() === 'closed') return;

    ws = new WebSocket(environment.chatAiWSUrl);
    ws.addEventListener('open',    onOpen);
    ws.addEventListener('message', onMessage);
    ws.addEventListener('close',   onClose);
    ws.addEventListener('error',   onSocketError);
  }

  // ── WebSocket event handlers ───────────────────────────────────────────────

  async function onOpen() {
    if (getState() === 'closed') { ws?.close(); return; }

    state = 'authenticating';

    const token = await getToken();
    if (!token) { failPending('auth_failed', 'No auth token available'); return; }

    const msg: WsAuthMessage = { type: 'auth', token };
    ws?.send(JSON.stringify(msg));
  }

  function onMessage(raw: MessageEvent) {
    if (getState() === 'closed') return;

    let event: WsStreamEvent;
    try {
      event = JSON.parse(raw.data as string) as WsStreamEvent;
    } catch {
      return; // ignore malformed frames
    }

    switch (event.type) {
      case 'auth_ok':
        reconnectAttempts = 0;
        state = 'ready';
        drainQueue();
        break;

      case 'thinking':
        pending?.callbacks.onThinking?.(event);
        break;

      case 'token':
        if (pending) pending.tokensReceived = true;
        pending?.callbacks.onToken?.(event.text);
        break;

      case 'done':
        pending?.callbacks.onDone?.(event);
        pending = null;
        state = 'ready';
        break;

      case 'error': {
        const { code } = event;

        if (code === 'validation_error') {
          // Non-recoverable for this question — surface error, stay connected
          pending?.callbacks.onError?.(event);
          pending = null;
          state = 'ready';
          return;
        }

        if (code === 'auth_failed' || code === 'timeout') {
          // Recoverable — reconnect will retry the question (if not mid-stream)
          scheduleReconnect(code === 'auth_failed');
          return;
        }

        // Unknown error code — treat as fatal for this question
        pending?.callbacks.onError?.(event);
        pending = null;
        state = 'ready';
        break;
      }
    }
  }

  function onClose() {
    if (getState() === 'closed') return;
    scheduleReconnect(false);
  }

  function onSocketError() {
    if (getState() === 'closed') return;
    scheduleReconnect(false);
  }

  // ── Reconnect ──────────────────────────────────────────────────────────────

  function scheduleReconnect(refreshToken: boolean) {
    if (getState() === 'closed') return;

    // If we were mid-stream (tokens already sent to UI), don't silently restart
    // the question — surface an error instead so the UI can react
    if (pending?.tokensReceived) {
      pending.callbacks.onError?.({
        type: 'error',
        code: 'timeout',
        message: 'Connection lost mid-stream',
      });
      pending = null;
    }

    if (reconnectAttempts >= MAX_RECONNECT_RETRIES) {
      failPending('timeout', 'Max reconnect attempts reached');
      state = 'closed';
      return;
    }

    reconnectAttempts++;
    state = 'reconnecting';
    destroySocket();

    const delay = RECONNECT_DELAY_MS * reconnectAttempts;
    setTimeout(async () => {
      if (getState() === 'closed') return;
      if (refreshToken) await getToken(); // force-refresh before connecting
      await connect();
    }, delay);
  }

  // ── Queue management ───────────────────────────────────────────────────────

  function drainQueue() {
    if (state !== 'ready' || !pending || !ws) return;
    state = 'busy';
    ws.send(JSON.stringify(pending.request));
  }

  function failPending(
    code: 'auth_failed' | 'timeout' | 'validation_error',
    message: string
  ) {
    if (!pending) return;
    pending.callbacks.onError?.({ type: 'error', code, message });
    pending = null;
  }

  // ── Socket cleanup ─────────────────────────────────────────────────────────

  function destroySocket() {
    if (!ws) return;
    ws.removeEventListener('open',    onOpen);
    ws.removeEventListener('message', onMessage);
    ws.removeEventListener('close',   onClose);
    ws.removeEventListener('error',   onSocketError);
    try { ws.close(); } catch { /* ignore */ }
    ws = null;
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  const ask = (request: WsChatRequest, callbacks: StreamCallbacks): (() => void) => {
    let cancelled = false;

    // Queue (overwrite) the pending question — one at a time
    pending = { request, callbacks, tokensReceived: false };

    if (state === 'ready') drainQueue();
    // If connecting / authenticating / reconnecting → drainQueue() fires after auth_ok

    return () => {
      if (cancelled) return;
      cancelled = true;
      if (pending?.request === request) {
        pending = null;
        // If already sent (busy), we can't un-send — just stop routing events
        if (state === 'busy') state = 'ready';
      }
    };
  };

  const close = () => {
    state = 'closed';
    pending = null;
    destroySocket();
  };

  // Kick off the first connection immediately
  connect();

  return { ask, close };
}
